import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  getWorkspaceColor,
  getWorkspaceInitials,
  type WorkspaceSwitchRailEntry
} from '@shared/utils/workspace-identity.util';
import type { WorkspaceSwitchMarker } from '@core/services/infra/authenticated-loading-coordinator.service';

/**
 * The "Rail" curtain for a workspace switch: the user's workspaces scroll
 * past on the brand gradient and settle under a sight on the destination,
 * whose name sits above a progress track. Rendered by
 * AuthenticatedLoadingScreenComponent in place of the minimal curtain,
 * exclusively when `uiMode === 'workspace-switch'`.
 *
 * No brand mark here on purpose — the composition this rideau specifies is
 * eyebrow / rail / destination name / progress, nothing else.
 */
@Component({
  selector: 'app-workspace-switch-curtain',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  host: {
    '[class.ws-curtain--solo]': '!showRail()'
  },
  template: `
    <div class="ws-curtain">
      <div class="ws-curtain__head">
        <p class="ws-curtain__eyebrow">{{ 'shared.workspaceSwitch.eyebrow' | translate }}</p>
        @if (marker().fromName) {
          <p class="ws-curtain__from">
            {{ 'shared.workspaceSwitch.from' | translate: { name: marker().fromName } }}
          </p>
        }
      </div>

      @if (showRail()) {
        <div class="ws-curtain__rail-block" aria-hidden="true">
          <div class="ws-rail-wrap" [style.--destination-index]="destinationIndex()">
            <div class="ws-rail">
              @for (tile of rail(); track tile.id) {
                <div class="ws-rail__col">
                  <div
                    class="ws-rail__tile"
                    [class.ws-rail__tile--dest]="tile.id === marker().toId"
                    [style.background]="tile.color"
                  >
                    <span>{{ tile.initials }}</span>
                  </div>
                  <div
                    class="ws-rail__label"
                    [class.ws-rail__label--dest]="tile.id === marker().toId"
                  >
                    {{ tile.name }}
                  </div>
                </div>
              }
            </div>
            <div class="ws-rail__sight"></div>
          </div>
        </div>
      } @else {
        <div class="ws-curtain__solo" aria-hidden="true">
          <div class="ws-rail__tile ws-rail__tile--dest" [style.background]="destination().color">
            <span>{{ destination().initials }}</span>
          </div>
        </div>
      }

      <h2 class="ws-curtain__name">{{ destination().name }}</h2>

      <div
        class="ws-curtain__track"
        [class.ws-curtain__track--indeterminate]="percent() === null"
        role="progressbar"
        [attr.aria-label]="'shared.workspaceSwitch.status' | translate: { name: destination().name }"
        [attr.aria-valuenow]="percent()"
        [attr.aria-valuemin]="percent() === null ? null : 0"
        [attr.aria-valuemax]="percent() === null ? null : 100"
      >
        <div class="ws-curtain__fill" [style.width.%]="percent()"></div>
      </div>
      <p class="ws-curtain__preparing">{{ 'shared.workspaceSwitch.preparing' | translate }}</p>

      <span role="status" class="sr-only">
        {{ 'shared.workspaceSwitch.status' | translate: { name: destination().name } }}
      </span>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    .ws-curtain {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 40px;
      /* Hard-coded, not tokens: var(--pp-ink) measured on dev.propor.io as
         resolving to the app's slate-950 body text colour, not white — even
         with an explicit fallback value in the var() itself, and even with
         !important. The applied stylesheet rule read back from the live DOM
         (document.styleSheets) did not match what shipped in the bundle,
         which points at some deeper runtime CSS custom-property handling
         specific to this component's *inline* styles (a sibling .scss file,
         brand-splash.component.scss, references the same --pp-ink and does
         resolve correctly) — not something to chase further inside a single
         component fix. These are the canonical brand layer's own values
         (shared/styles/_brand-splash.generated.scss), typed directly. */
      color: #FFFFFF;
      text-align: center;
    }

    .ws-curtain__head { margin: 0; }
    .ws-curtain__eyebrow {
      font: 700 12px/1 sans-serif;
      letter-spacing: .16em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, .78);
      margin: 0;
    }
    .ws-curtain__from {
      margin: 6px 0 0;
      font: 500 13px/1.3 sans-serif;
      color: rgba(255, 255, 255, .78);
    }

    /* ---- Rail geometry — single source of truth: only --tile/--gap/--col-w/
       --container-h/--sight ever change (per breakpoint below); everything
       else is derived so the geometry invariant (sight never overlaps the
       label) holds at every size. ---- */
    .ws-rail-wrap {
      position: relative;
      height: var(--container-h);
      width: min(90vw, 640px);
      overflow: hidden;
      /* Fades the tile strip itself to transparent at both edges, revealing
         the ambient gradient underneath exactly as it already is — no need
         to sample or guess its colour (that guess is what produced a visible
         rectangle: the diagonal gradient's real colour at this position
         didn't match the flat left/right hex it was approximated with). */
      -webkit-mask-image: linear-gradient(to right, transparent, black 56px, black calc(100% - 56px), transparent);
      mask-image: linear-gradient(to right, transparent, black 56px, black calc(100% - 56px), transparent);
      --tile: 74px;
      --gap: 24px;
      --col-w: 102px;
      --container-h: 150px;
      --sight: 82px;
      --label-gap: 9px;
      --label-h: 28px;

      --step: calc(var(--col-w) + var(--gap));
      --col-h: calc(var(--tile) + var(--label-gap) + var(--label-h));
      --col-top: calc((var(--container-h) - var(--col-h)) / 2);
      --tile-center: calc(var(--col-top) + var(--tile) / 2);
      --sight-top: calc(var(--tile-center) - var(--sight) / 2);
      --sight-left: calc(50% - var(--sight) / 2);
      --rail-offset: calc(var(--destination-index, 0) * var(--step));
    }

    .ws-rail {
      display: flex;
      align-items: flex-start;
      gap: var(--gap);
      padding-top: var(--col-top);
      /* Each column is --col-w wide with the tile centred inside it, so the
         destination COLUMN (not the narrower tile) is what must land under
         the sight. --destination-index * --step cancels out of this once the
         translateX(-offset) is applied, leaving a single fixed rest position
         independent of the index — so the sight itself never needs to enter
         this formula at all: centre the first column, and every destination
         column lands there after the shift. */
      padding-left: calc(50% - var(--col-w) / 2);
      animation: wsRailSettle 1.9s cubic-bezier(.2, .8, .2, 1) both;
    }

    .ws-rail__col {
      width: var(--col-w);
      flex: none;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .ws-rail__tile {
      width: var(--tile);
      height: var(--tile);
      flex: none;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: .4;
      font: 700 24px/1 sans-serif;
      color: #FFFFFF;
    }
    .ws-rail__tile--dest {
      opacity: 1;
      font-weight: 800;
      box-shadow: 0 18px 34px -10px rgba(15, 23, 41, .45);
    }

    .ws-rail__label {
      margin-top: var(--label-gap);
      height: var(--label-h);
      font: 500 11px/1.25 sans-serif;
      text-align: center;
      color: rgba(255, 255, 255, .78);
      overflow: hidden;
    }
    .ws-rail__label--dest {
      font-weight: 700;
      color: #FFFFFF;
    }

    .ws-rail__sight {
      position: absolute;
      top: var(--sight-top);
      left: var(--sight-left);
      width: var(--sight);
      height: var(--sight);
      box-sizing: border-box;
      border: 2px solid #FFFFFF;
      border-radius: 20px;
      box-shadow: 0 0 0 5px rgba(255, 255, 255, .18);
      pointer-events: none;
    }

    @keyframes wsRailSettle {
      0% { transform: translateX(0); }
      55% { transform: translateX(calc(-1 * var(--rail-offset))); }
      100% { transform: translateX(calc(-1 * var(--rail-offset))); }
    }

    /* ---- Solo fallback: rail absent, < 2 entries, or < 600px ---- */
    .ws-curtain__solo { display: none; }
    :host(.ws-curtain--solo) .ws-curtain__rail-block { display: none; }
    :host(.ws-curtain--solo) .ws-curtain__solo { display: flex; justify-content: center; }

    /* ---- Destination name + progress ---- */
    .ws-curtain__name {
      margin: 0;
      /* !important: this is an <h2>, and the global Material typography
         rule on bare h2 (src/@vex/styles/_themes.scss) also sets its own
         colour — a second, independent reason this could lose. */
      color: #FFFFFF !important;
      font: 800 26px/1.2 sans-serif;
      letter-spacing: -.02em;
    }

    .ws-curtain__track {
      width: 216px;
      height: 5px;
      border-radius: 999px;
      background: rgba(255, 255, 255, .22);
      overflow: hidden;
      position: relative;
    }
    .ws-curtain__fill {
      height: 100%;
      background: #FFFFFF;
      transition: width .3s ease;
    }
    .ws-curtain__track--indeterminate .ws-curtain__fill {
      width: 40%;
      transition: none;
      animation: wsCurtainShuttle 1.6s ease-in-out infinite;
    }
    /* Same duration/easing/fill% as brand-splash's shuttle, kept under a
       distinct keyframe name: Angular doesn't scope @keyframes identifiers
       per component even under emulated encapsulation, so sharing the literal
       name across two independently-styled components is a real collision
       risk, not a safe reuse. */
    @keyframes wsCurtainShuttle {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(250%); }
    }

    .ws-curtain__preparing {
      margin: 8px 0 0;
      font: 500 13px/1.3 sans-serif;
      color: rgba(255, 255, 255, .78);
    }

    /* ---- Breakpoints — only the geometry inputs move ---- */
    @media (max-width: 899.98px) {
      .ws-rail-wrap {
        --tile: 62px;
        --gap: 16px;
        --col-w: 62px;
        --container-h: 120px;
        --sight: 70px;
      }
    }
    @media (max-width: 599.98px) {
      .ws-curtain__rail-block { display: none; }
      .ws-curtain__solo { display: flex; justify-content: center; }
    }
    @media (max-height: 380px) {
      .ws-curtain__eyebrow { display: none; }
    }
    @media (prefers-reduced-motion: reduce) {
      .ws-rail {
        animation: none;
        transform: translateX(calc(-1 * var(--rail-offset)));
      }
      .ws-curtain__track--indeterminate .ws-curtain__fill {
        animation: none;
        width: 40%;
      }
    }
  `]
})
export class WorkspaceSwitchCurtainComponent {
  readonly marker = input.required<WorkspaceSwitchMarker>();
  readonly progress = input<number | null>(null);

  readonly rail = computed(() => this.marker().rail ?? []);

  readonly destinationIndex = computed(() =>
    this.rail().findIndex((r) => r.id === this.marker().toId)
  );

  readonly showRail = computed(
    () => this.rail().length >= 2 && this.destinationIndex() !== -1
  );

  readonly destination = computed<WorkspaceSwitchRailEntry>(() => {
    const idx = this.destinationIndex();
    if (idx !== -1) return this.rail()[idx];
    const name = this.marker().toName;
    return {
      id: this.marker().toId || 'destination',
      name,
      initials: getWorkspaceInitials({ name }),
      color: getWorkspaceColor({ name })
    };
  });

  readonly percent = computed(() => {
    const p = this.progress();
    return p === null || p === undefined
      ? null
      : Math.round(Math.min(100, Math.max(0, p)));
  });
}
